// The tenant-keyed cache wrappers — the WRAPPER layer where TanStack Query is used directly (feature
// code may not, per the no-raw-useQuery lint rule; this file lives under app/, outside the rule's scope). Every
// band-scoped hook ('s useGuestList, and on) builds on these, so the query key is ALWAYS
// ['band', bandId, ...] with bandId read from the mounted BandContext — a module structurally cannot fetch
// outside its tenant (the MVP's cross-artist render bleed becomes unwritable).
import {
  useMutation,
  useQuery,
  type UseMutationOptions,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { useBandContext } from "../bandContext.js";

/**
 * A band-scoped query. The caller passes the key SUFFIX (e.g. `['show', showId, 'guests']`); this prepends
 * `['band', bandId, ...]` from the server BandContext (never a client-selected value). The query fn
 * receives the resolved bandId so hooks can pass it to the generated client's path params.
 */
export function useBandQuery<TData>(
  keySuffix: readonly unknown[],
  queryFn: (bandId: string) => Promise<TData>,
  options?: Omit<UseQueryOptions<TData, Error>, "queryKey" | "queryFn">,
) {
  const { bandId } = useBandContext();
  return useQuery<TData, Error>({
    queryKey: ["band", bandId, ...keySuffix],
    queryFn: () => queryFn(bandId),
    ...options,
  });
}

/**
 * A band-scoped mutation — the mutation fn receives the resolved tenant bandId alongside its variables.
 * `TContext` carries the onMutate snapshot through onError/onSettled so optimistic hooks ('s
 * useGuestList) can roll back the pre-mutation cache on failure; it defaults to `unknown`, so
 * existing non-optimistic callers are unaffected.
 */
export function useBandMutation<TData, TVariables, TContext = unknown>(
  mutationFn: (bandId: string, variables: TVariables) => Promise<TData>,
  options?: Omit<UseMutationOptions<TData, Error, TVariables, TContext>, "mutationFn">,
) {
  const { bandId } = useBandContext();
  return useMutation<TData, Error, TVariables, TContext>({
    mutationFn: (variables) => mutationFn(bandId, variables),
    ...options,
  });
}
